/**
 * Author: Fleming Shi
 * Project: DAL (Digital Abstraction Layer)
 * Date: 2025-11-08
 * Revision: 1.4
 * Description: Data Integration page with import/export, KT matrix, and activity tracking
 */

import React, { useState } from 'react'
import { Upload, FileSpreadsheet, AlertCircle, Download, RefreshCw, Trash2 } from 'lucide-react'
import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'

interface Activity {
  id: string
  type: 'import' | 'download'
  description: string
  timestamp: Date
  status: 'success' | 'error'
}

const DataIntegration: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isPurging, setIsPurging] = useState(false)
  const [isReindexing, setIsReindexing] = useState(false)
  const [activities, setActivities] = useState<Activity[]>([])

  const addActivity = (type: 'import' | 'download', description: string, status: 'success' | 'error') => {
    const newActivity: Activity = {
      id: Date.now().toString(),
      type,
      description,
      timestamp: new Date(),
      status,
    }
    setActivities((prev) => [newActivity, ...prev].slice(0, 10)) // Keep only last 10 activities
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFileSelection(files[0])
    }
  }

  const handleFileSelection = (file: File) => {
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
    ]

    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error('Please upload a valid Excel file (.xlsx, .xls, or .csv)')
      return
    }

    setSelectedFile(file)
    toast.success('File selected successfully')
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      handleFileSelection(files[0])
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first')
      return
    }

    setIsUploading(true)

    try {
      const formData = new FormData()
      formData.append('file', selectedFile)

      const API_BASE_URL =
        typeof window !== 'undefined' && window.location.protocol === 'https:'
          ? `${window.location.origin}/api`
          : import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api'

      // Get access token from authService
      const { getAccessToken } = await import('../services/authService')
      const token = await getAccessToken()

      const response = await fetch(`${API_BASE_URL}/systems/bulk-import`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Failed to upload file')
      }

      // Show detailed results
      const { successful, failed, errors } = result.data

      if (failed > 0) {
        toast.warning(
          `Import completed with warnings: ${successful} systems imported, ${failed} failed. Check console for details.`,
          { autoClose: 5000 },
        )
        console.error('Import errors:', errors)
        addActivity('import', `Imported ${successful} systems (${failed} failed) from ${selectedFile.name}`, 'success')
      } else {
        toast.success(`Import successful! ${successful} systems imported.`)
        addActivity('import', `Imported ${successful} systems from ${selectedFile.name}`, 'success')
      }

      setSelectedFile(null)
    } catch (error) {
      console.error('Upload error:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to upload file')
      addActivity('import', `Failed to import from ${selectedFile?.name || 'file'}`, 'error')
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    toast.info('File removed')
  }

  const handleDownloadTemplate = () => {
    try {
      // Define the column headers based on the System interface
      const headers = [
        'System Name',
        'Description',
        'Origin',
        'Date Acquired Year',
        'Acquisition Type',
        'Custom Type',
        'User Interfaces',
        'Maintenance Schedule',
        'Business Owner',
        'Technical Product Owner',
        'Management Type',
        'Segment Channel',
        'Customers Impacted',
        'Monthly Volume',
        'Customer Footprint',
        'Product Related Notes',
        'Other User Groups',
        'Site Capability',
        'Geographic Scope',
        'Limitations',
        'Integration Capabilities',
        'Dependencies',
        'Impacted Subsystems',
        'Northstar Alignment',
        'Shared Resources Non CB',
        'Security Score',
        'Compliance Score',
      ]

      // Add example/instruction row
      const exampleRow = [
        'Example System Name',
        'System description here',
        'Origin of the system',
        '2023',
        'acquisition | homegrown | other',
        'custom | vendor_partnership | off_the_shelf | other',
        'Web, Mobile, Desktop',
        'Monthly, Quarterly, Annual',
        'John Doe',
        'Jane Smith',
        'BIT | CB | shared | other',
        'Enterprise, SMB, Consumer',
        'All customers',
        '10000',
        'National',
        'Product A, Product B',
        'Internal teams',
        'E-commerce, CRM',
        'international | domestic | both | other',
        'Known limitations',
        'REST API, GraphQL',
        'Database, Auth Service',
        'Payment Gateway',
        'Aligned with strategy',
        'true | false',
        'low | medium | high',
        'low | medium | high',
      ]

      // Create worksheet
      const ws = XLSX.utils.aoa_to_sheet([headers, exampleRow])

      // Set column widths
      const colWidths = headers.map(() => ({ wch: 25 }))
      ws['!cols'] = colWidths

      // Create workbook and add worksheet
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Systems Template')

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0]
      const filename = `DAL_Systems_Import_Template_${timestamp}.xlsx`

      // Download file
      XLSX.writeFile(wb, filename)

      toast.success('Template downloaded successfully')
      addActivity('download', `Downloaded import template: ${filename}`, 'success')
    } catch (error) {
      console.error('Template download error:', error)
      toast.error('Failed to download template')
      addActivity('download', 'Failed to download import template', 'error')
    }
  }

  const handleDownloadKTFormat = async () => {
    try {
      toast.info('Generating KT format export...')

      // Import api dynamically to avoid circular dependency issues
      const { api } = await import('../services/api')

      // Fetch all systems and products
      const [systems, products] = await Promise.all([api.getAllSystems(), api.getAllProducts()])

      // Sort systems and products alphabetically
      const sortedSystems = [...systems].sort((a, b) => a.system_name.localeCompare(b.system_name))
      const sortedProducts = [...products].sort((a, b) => a.product_name.localeCompare(b.product_name))

      // Create the matrix data structure
      const matrix: any[][] = []

      // Row 1: Empty cell in A1, then system names starting from B1
      const headerRow = ['', ...sortedSystems.map((s) => s.system_name)]
      matrix.push(headerRow)

      // For each product, create a row
      for (const product of sortedProducts) {
        const row = [product.product_name]

        // For each system, check if this product is linked
        for (const system of sortedSystems) {
          if (system.id) {
            try {
              const linkedProducts = await api.getSystemProducts(system.id)
              const isLinked = linkedProducts.some((p) => p.id === product.id)
              row.push(isLinked ? 'Yes' : '')
            } catch (error) {
              row.push('')
            }
          } else {
            row.push('')
          }
        }

        matrix.push(row)
      }

      // Create worksheet from matrix
      const ws = XLSX.utils.aoa_to_sheet(matrix)

      // Set column widths
      const colWidths = [
        { wch: 30 }, // Product name column
        ...sortedSystems.map(() => ({ wch: 20 })), // System columns
      ]
      ws['!cols'] = colWidths

      // Create workbook and add worksheet
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'KT Matrix')

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0]
      const filename = `DAL_KT_Matrix_${timestamp}.xlsx`

      // Download file
      XLSX.writeFile(wb, filename)

      toast.success('KT format exported successfully')
      addActivity(
        'download',
        `Downloaded KT matrix: ${filename} (${sortedSystems.length} systems, ${sortedProducts.length} products)`,
        'success',
      )
    } catch (error) {
      console.error('KT format download error:', error)
      toast.error('Failed to export KT format')
      addActivity('download', 'Failed to export KT matrix', 'error')
    }
  }

  const handlePurgeIndex = async () => {
    if (!confirm('Are you sure you want to purge all indexed context data? This cannot be undone.')) return
    setIsPurging(true)
    try {
      const { api } = await import('@services/api')
      const result = await api.purgeContextIndex()
      toast.success(`Purged ${result.deletedCount} indexed document(s)`)
      addActivity('download', `Purged ${result.deletedCount} context index entries`, 'success')
    } catch (error) {
      console.error('Purge error:', error)
      toast.error('Failed to purge context index')
      addActivity('download', 'Failed to purge context index', 'error')
    } finally {
      setIsPurging(false)
    }
  }

  const handleReindex = async () => {
    setIsReindexing(true)
    try {
      const { api } = await import('@services/api')
      const result = await api.reindexContext()
      toast.success(`Indexed ${result.systems} systems and ${result.products} products`)
      addActivity(
        'import',
        `Reindexed ${result.indexed} items (${result.systems} systems, ${result.products} products)`,
        'success',
      )
    } catch (error) {
      console.error('Reindex error:', error)
      toast.error('Failed to reindex context data')
      addActivity('import', 'Failed to reindex context data', 'error')
    } finally {
      setIsReindexing(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Data Integration</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Import and export system data in bulk</p>
      </div>

      {/* Bulk Import Section */}
      <div className="card">
        <h3 className="mb-4 flex items-center space-x-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
          <Upload className="h-5 w-5" />
          <span>Bulk Import</span>
        </h3>
        <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
          Upload an Excel file containing system information to import multiple systems at once. Supported formats:
          .xlsx, .xls, .csv
        </p>

        {/* Instructions */}
        <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800/50 dark:bg-blue-900/30">
          <h4 className="mb-2 text-sm font-semibold text-blue-900 dark:text-blue-300">File Format Requirements:</h4>
          <ul className="list-inside list-disc space-y-1 text-sm text-blue-800 dark:text-blue-300">
            <li>First row must contain column headers</li>
            <li>Required columns: System Name, Business Owner</li>
            <li>
              Optional columns: Acquisition Type, Custom Type, Management Type, Geographic Scope, Security Score,
              Compliance Score
            </li>
            <li>Maximum file size: 10MB</li>
          </ul>
        </div>

        {/* Upload Area */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
            isDragging
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-300 bg-gray-50 hover:border-gray-400 dark:border-gray-600 dark:bg-white/5'
          }`}
        >
          {selectedFile ? (
            <div className="space-y-4">
              <FileSpreadsheet className="mx-auto h-12 w-12 text-green-600" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedFile.name}</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {(selectedFile.size / 1024).toFixed(2)} KB
                </p>
              </div>
              <div className="flex justify-center space-x-3">
                <button onClick={handleUpload} disabled={isUploading} className="btn-primary cursor-pointer">
                  {isUploading ? (
                    <>
                      <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                      Uploading...
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Upload className="mr-2 h-4 w-4" />
                      Upload & Import
                    </div>
                  )}
                </button>
                <button
                  onClick={handleRemoveFile}
                  disabled={isUploading}
                  className="cursor-pointer rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-900"
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <Upload className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
              <div>
                <label htmlFor="file-upload" className="cursor-pointer">
                  <span className="text-primary-600 hover:text-primary-700 font-medium">Choose a file</span>
                  <span className="text-gray-600 dark:text-gray-400"> or drag and drop</span>
                  <input
                    id="file-upload"
                    type="file"
                    className="hidden"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileInput}
                  />
                </label>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Excel files (.xlsx, .xls) or CSV files up to 10MB
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Sample Template */}
        <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-white/5">
          <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">Need a template?</h4>
          <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
            Download our sample Excel template with all system attributes and example data.
          </p>
          <button
            className="btn-primary flex cursor-pointer items-center space-x-2 text-sm"
            onClick={handleDownloadTemplate}
          >
            <Download className="h-4 w-4" />
            <span>Download Sample Template</span>
          </button>
        </div>
      </div>

      {/* KT Format Export Section */}
      <div className="card">
        <h3 className="mb-4 flex items-center space-x-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
          <Download className="h-5 w-5" />
          <span>KT Format Download</span>
        </h3>
        <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
          Export all systems and products in the KT matrix format. Systems will be listed in the top row, products in
          the first column, with cells marked "Yes" where a product is supported by a system.
        </p>

        <div className="mb-6 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800/50 dark:bg-green-900/30">
          <h4 className="mb-2 text-sm font-semibold text-green-900 dark:text-green-300">Export Format:</h4>
          <ul className="list-inside list-disc space-y-1 text-sm text-green-800 dark:text-green-300">
            <li>Row 1: System names (starting from column B)</li>
            <li>Column A: Product names (starting from row 2)</li>
            <li>Cells marked "Yes" indicate product-system relationships</li>
            <li>All systems and products sorted alphabetically</li>
          </ul>
        </div>

        <button className="btn-primary flex cursor-pointer items-center space-x-2" onClick={handleDownloadKTFormat}>
          <Download className="h-5 w-5" />
          <span>Download KT Matrix</span>
        </button>
      </div>

      {/* LLM Context Index */}
      <div className="card">
        <h3 className="mb-4 flex items-center space-x-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
          <RefreshCw className="h-5 w-5" />
          <span>LLM Context Index</span>
        </h3>
        <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
          Index systems and products data for the AI chatbot. Reindexing generates searchable text entries so the
          chatbot can find relevant context when answering questions.
        </p>

        <div className="flex flex-wrap gap-3">
          <button
            className="btn-primary flex cursor-pointer items-center space-x-2"
            onClick={handleReindex}
            disabled={isReindexing}
          >
            <RefreshCw className={`h-5 w-5 ${isReindexing ? 'animate-spin' : ''}`} />
            <span>{isReindexing ? 'Reindexing...' : 'Reindex Data'}</span>
          </button>

          <button
            className="flex cursor-pointer items-center space-x-2 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:bg-gray-800 dark:text-red-300 dark:hover:bg-red-900/30"
            onClick={handlePurgeIndex}
            disabled={isPurging}
          >
            <Trash2 className="h-5 w-5" />
            <span>{isPurging ? 'Purging...' : 'Purge Index'}</span>
          </button>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="card">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Recent Activities</h3>
        {activities.length === 0 ? (
          <div className="py-8 text-center">
            <AlertCircle className="mx-auto mb-3 h-12 w-12 text-gray-400 dark:text-gray-500" />
            <p className="text-gray-500 dark:text-gray-400">No recent activities</p>
            <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">Your imports and downloads will appear here</p>
          </div>
        ) : (
          <div className="space-y-2">
            {activities.map((activity) => (
              <div
                key={activity.id}
                className={`flex items-start space-x-3 rounded-lg border p-3 ${
                  activity.status === 'success'
                    ? 'border-green-200 bg-green-50 dark:border-green-800/50 dark:bg-green-900/30'
                    : 'border-red-200 bg-red-50 dark:border-red-800/50 dark:bg-red-900/30'
                }`}
              >
                <div className="mt-0.5 flex-shrink-0">
                  {activity.type === 'import' ? (
                    <Upload
                      className={`h-5 w-5 ${activity.status === 'success' ? 'text-green-600' : 'text-red-600'}`}
                    />
                  ) : (
                    <Download
                      className={`h-5 w-5 ${activity.status === 'success' ? 'text-green-600' : 'text-red-600'}`}
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-sm font-medium ${activity.status === 'success' ? 'text-green-900 dark:text-green-300' : 'text-red-900 dark:text-red-300'}`}
                  >
                    {activity.description}
                  </p>
                  <p className={`mt-1 text-xs ${activity.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                    {activity.timestamp.toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      activity.status === 'success'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                    }`}
                  >
                    {activity.type === 'import' ? 'Import' : 'Download'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default DataIntegration
